import type { ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { useAuth } from '@/contexts/AuthContext';
import { AuthRedirect } from '@/components/auth/AuthRedirect';
import { ViewLoadingFallback } from '@/components/ui/ViewLoadingFallback';

type AuthRouteGate =
  | { fallback: ReactNode; user: null }
  | { fallback: null; user: User };

/**
 * Shared auth gate for protected routes. Returns a fallback to render
 * (loading spinner or AuthRedirect) when the user is not yet authenticated,
 * or null + the user when the route can proceed. A pending OAuth error stays
 * in AuthContext; the /auth page consumes and clears it.
 */
export function useAuthRouteGate(): AuthRouteGate {
  const { user, isLoading, isProcessingOAuth, isAuthReady } = useAuth();
  const shouldShowLoading = isLoading || isProcessingOAuth || (!isAuthReady && !user);

  if (shouldShowLoading) {
    return { fallback: <ViewLoadingFallback />, user: null };
  }

  if (!user) {
    return {
      fallback: <AuthRedirect />,
      user: null,
    };
  }

  return { fallback: null, user };
}
